import { useEffect, useRef } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { Meadow } from '../../yosemite/meadow';
import { useGameStore } from '../../core/store/gameStore';

// ============================================================================
// MeadowWorld - the default world for showing SketchUp imports: an open grass
// meadow with distant hazy ridges. No DEM / satellite data is fetched, so it
// mounts immediately and works offline. Keeps the same readiness target id
// ("yosemite") so LoadingScreen and the ready-gate are unchanged; only the
// visuals/content differ from the former full valley.
// ============================================================================

export function MeadowWorld() {
  const scene = useThree((s) => s.scene);
  const isMobile = useGameStore((state) => state.isMobile);
  const quality = useGameStore((state) => state.quality);
  const setTerrain = useGameStore((state) => state.setTerrain);
  const setComponentReady = useGameStore((state) => state.setComponentReady);

  const meadowRef = useRef<Meadow | null>(null);

  useEffect(() => {
    let cancelled = false;

    const build = async () => {
      try {
        const meadow = new Meadow(isMobile ? 'low' : quality);
        if (cancelled) {
          meadow.dispose();
          return;
        }
        scene.add(meadow.group);
        setTerrain(meadow as any);
        meadowRef.current = meadow;

        // Gentle aerial haze so the far ridges melt into the skyline.
        const fog = new THREE.Fog(0xcfe0ee, 900, 4300);
        scene.fog = fog;

        setComponentReady('yosemite', true);
      } catch (err) {
        console.error('Failed to build meadow world:', err);
        setComponentReady('yosemite', true);
      }
    };

    build();

    return () => {
      cancelled = true;
      scene.fog = null;
      const m = meadowRef.current;
      if (m) {
        scene.remove(m.group);
        m.dispose();
        meadowRef.current = null;
      }
      setTerrain(null);
      setComponentReady('yosemite', false);
    };
  }, [scene, isMobile, quality, setTerrain, setComponentReady]);

  return null;
}
